import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { SchemaGenerator } from '../../components/SchemaGenerator';
import { Footer } from '../../components/Footer';
import { MainNavigation } from '../../components/MainNavigation';
import { GridBackground } from '../../components/GridBackground';
import { CTA1, CTA2 } from '../../components/CTABanners';
import { AISnippetBlockFeatured } from '../../components/AISnippetBlock';
import { PeopleAlsoAsk } from '../../components/PeopleAlsoAsk';
import { BreadcrumbNav } from '../../components/BreadcrumbNav';
import { PillarContentRenderer } from '../../components/PillarContentRenderer';
import { ArticleFooter } from '../../components/ArticleFooter';
import { loadAllArticles } from '@/services/articleLoader';
import { bestIPTVFirestick2026 } from '../../data/pillarPages/bestIPTVFirestick2026';

const PILLAR_SLUG = 'best-iptv-firestick-2026';

export const BestIPTVFirestick2026Page = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('installation');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const articles = await loadAllArticles();
        const found = articles.find(a => a.slug === PILLAR_SLUG);
        if (mounted) {
          setArticle(found || null);
          setLoading(false);
        }
      } catch (e) {
        console.warn('Failed to load pillar article from /articles:', e);
        if (mounted) {
          setArticle(null);
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Loading article...
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Schema generation for all required types
  const schemas = [
    {
      type: 'Article',
      data: {
        headline: article.title,
        description: article.excerpt,
        image: bestIPTVFirestick2026.schema.article.image,
        author: bestIPTVFirestick2026.schema.article.author,
        datePublished: article.date,
        dateModified: bestIPTVFirestick2026.lastUpdated || article.date
      }
    },
    {
      type: 'FAQ',
      data: {
        faqs: (article.faqItems && article.faqItems.length ? article.faqItems : bestIPTVFirestick2026.faqs).map(faq => ({
          question: faq.question,
          answer: faq.answer
        }))
      }
    },
    {
      type: 'HowTo',
      data: bestIPTVFirestick2026.schema.howTo
    },
    {
      type: 'BreadcrumbList',
      data: {
        breadcrumbs: bestIPTVFirestick2026.breadcrumbs
      }
    }
  ];

  return (
    <>
      <Helmet>
        <title>{article.title}</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta name="keywords" content={(article.seoKeywords || []).join(', ')} />
        <link rel="canonical" href={`https://xtreamgate.com/blog/${article.slug}`} />
      </Helmet>

      {/* Schema Markup */}
      {schemas.map((schema, idx) => (
        <SchemaGenerator key={idx} type={schema.type} data={schema.data} />
      ))}

      <div className="min-h-screen relative">
        <GridBackground />
        <MainNavigation />

        {/* Main Content */}
        <motion.div
          className="px-4 py-8 relative z-10"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb Navigation */}
            <BreadcrumbNav items={bestIPTVFirestick2026.breadcrumbs} />

            {/* Header */}
            <motion.div variants={itemVariants} className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 bg-primary/20 border border-primary/30 text-primary text-sm font-semibold rounded-full">
                  {article.category}
                </span>
                <span className="text-slate-400 text-sm">{article.readTime}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-xl text-slate-300">
                {article.excerpt}
              </p>
            </motion.div>

            {/* AI Snippet Block - Featured */}
            <motion.div variants={itemVariants} className="mb-8">
              <AISnippetBlockFeatured
                quickAnswer={article.quickAnswer}
                keyPoints={article.keyPoints}
                title="Quick Answer"
              />
            </motion.div>

            {/* Expanded Answer */}
            <motion.div
              variants={itemVariants}
              className="mb-12 p-6 rounded-lg bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 backdrop-blur-sm"
            >
              <h2 className="text-2xl font-bold text-white mb-4">Why Firestick is Perfect for IPTV</h2>
              <PillarContentRenderer content={bestIPTVFirestick2026.expandedAnswer} />
            </motion.div>

            {/* Setup Guide Section */}
            <motion.div variants={itemVariants} className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">8-Step Setup Guide</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bestIPTVFirestick2026.setupGuide.map((step, idx) => (
                  <motion.div
                    key={idx}
                    className="p-4 rounded-lg bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 hover:border-blue-500/60 transition-all"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{step.position}</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-white mb-2">{step.name}</h3>
                        <p className="text-slate-300 text-sm">{step.text}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Main Content Sections */}
            <motion.div variants={itemVariants} className="mb-12 space-y-8">
              {/* Section 1: Top IPTV Apps */}
              <div className="rounded-lg overflow-hidden bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 p-6">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {bestIPTVFirestick2026.mainContent.section1.heading}
                </h2>
                <PillarContentRenderer content={bestIPTVFirestick2026.mainContent.section1.content} />
              </div>

              {/* Section 2: Hardware Recommendations */}
              <div className="rounded-lg overflow-hidden bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 p-6">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {bestIPTVFirestick2026.mainContent.section2.heading}
                </h2>
                <PillarContentRenderer content={bestIPTVFirestick2026.mainContent.section2.content} />
              </div>

              {/* Section 3: Installation Methods */}
              <div className="rounded-lg overflow-hidden bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 p-6">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {bestIPTVFirestick2026.mainContent.section3.heading}
                </h2>
                <PillarContentRenderer content={bestIPTVFirestick2026.mainContent.section3.content} />
              </div>

              {/* Section 4: 4K Optimization */}
              <div className="rounded-lg overflow-hidden bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 p-6">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {bestIPTVFirestick2026.mainContent.section4.heading}
                </h2>
                <PillarContentRenderer content={bestIPTVFirestick2026.mainContent.section4.content} />
              </div>

              {/* Section 5: Troubleshooting */}
              <div className="rounded-lg overflow-hidden bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 p-6">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {bestIPTVFirestick2026.mainContent.section5.heading}
                </h2>
                <PillarContentRenderer content={bestIPTVFirestick2026.mainContent.section5.content} />
              </div>

              {/* Section 6: Why XtreamCube */}
              <div className="rounded-lg overflow-hidden bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 p-6">
                <h2 className="text-2xl font-bold text-white mb-4">
                  {bestIPTVFirestick2026.mainContent.section6.heading}
                </h2>
                <PillarContentRenderer content={bestIPTVFirestick2026.mainContent.section6.content} />
              </div>
            </motion.div>

            {/* Primary CTA */}
            <motion.div variants={itemVariants} className="mb-12">
              <CTA2
                headline={bestIPTVFirestick2026.cta.primary.text}
                subtext={bestIPTVFirestick2026.cta.primary.subtext}
                buttonText={bestIPTVFirestick2026.cta.primary.buttonText}
                buttonUrl={bestIPTVFirestick2026.cta.primary.buttonUrl}
              />
            </motion.div>

            {/* People Also Ask - FAQ */}
            <motion.div variants={itemVariants} className="mb-12">
              <PeopleAlsoAsk
                faqItems={article.faqItems && article.faqItems.length ? article.faqItems : bestIPTVFirestick2026.faqs}
              />
            </motion.div>

            {/* Internal Links */}
            <motion.div variants={itemVariants} className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Related Guides</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bestIPTVFirestick2026.internalLinks.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    className="p-4 rounded-lg bg-slate-800 border border-slate-700 hover:border-blue-500 transition-all group"
                  >
                    <h3 className="font-bold text-white group-hover:text-blue-400 transition-colors">
                      {link.text}
                    </h3>
                    <p className="text-slate-400 text-sm mt-2">{link.context}</p>
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Secondary CTA */}
            <motion.div variants={itemVariants} className="mb-12">
              <CTA1
                headline={bestIPTVFirestick2026.cta.secondary.text}
                subtext={bestIPTVFirestick2026.cta.secondary.subtext}
                buttonText={bestIPTVFirestick2026.cta.secondary.buttonText}
                buttonUrl={bestIPTVFirestick2026.cta.secondary.buttonUrl}
              />
            </motion.div>

            {/* Article Footer - Share & Navigation */}
            <ArticleFooter
              title={bestIPTVFirestick2026.title}
              nextArticleTitle="Best IPTV USA 2026"
              nextArticleUrl="/blog/best-iptv-usa-2026"
            />
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default BestIPTVFirestick2026Page;
